import type { ReactNode } from 'react';
import { CodeChip } from '../ui/CodeChip';
import { entityHue } from '../ui/EntityAvatar';

/**
 * A TERRITORY IN A PICKER — the same treatment warehouses already get (`warehouseLabel.tsx`).
 *
 * A beat has two names for one thing: the code staff quote ("MH-SANGAMNER") and the place a reader
 * recognises ("Sangamner"). Every territory dropdown in the app joined them with a dot, so a list
 * read as a column of "MH-AHMEDNAGAR · Ahmednagar", "GJ-AHMEDABAD · Ahmedabad" — one run-on string
 * per row with nothing for the eye to run down, and the selected value truncated to
 * "MH-SANGAMNER · S…" in the collapsed trigger (owner, 2026-09-29: "it is not looking good").
 *
 * So the code becomes a chip at the left edge — its own shape is the separator, no dot — and the
 * place name stays plain text. The chip's hue is the identity hue, so MH-SANGAMNER is the same
 * colour in every picker, chip and avatar in the app.
 */

type TerritoryLike = { code?: string | null; name?: string | null };

/** "MH-SANGAMNER · Sangamner" — the plain-text form, for search, a `title` tooltip and prose. */
export const territoryText = (t: TerritoryLike): string =>
  [t.code, t.name].map((v) => String(v ?? '').trim()).filter(Boolean).join(' · ') || '—';

/** The territory CODE as a chip, in that territory's own colour. */
export function TerritoryCode({ code }: { code: string }) {
  return <CodeChip hue={entityHue(code)} className="shrink-0 min-w-[6.5rem] text-center">{code}</CodeChip>;
}

/**
 * Label node for a territory option: the code chip then the place name.
 *
 * Block-level and `max-w-full` for the same reason the warehouse label is: a ui/Select's collapsed
 * trigger wraps the label in a `truncate` span, which only ellipsises INLINE content — an
 * inline-flex label sized to its own content overflows and is hard-clipped mid-word. Constrained,
 * the pressure lands on the NAME, and the code (what tells two beats apart) stays whole.
 */
export function territoryLabel(t: TerritoryLike): ReactNode {
  const code = String(t.code ?? '').trim();
  const name = String(t.name ?? '').trim();
  if (!code && !name) return '—';
  return (
    <span className="flex min-w-0 max-w-full items-center gap-1.5">
      {code ? <TerritoryCode code={code} /> : null}
      {name ? <span className="min-w-0 truncate">{name}</span> : null}
    </span>
  );
}

/**
 * Everything one territory option needs. Spread it: `{ value: String(t.id), ...territoryOption(t) }`.
 *
 * `selectedText` is what the COLLAPSED trigger shows, and it is the place name alone. An inline-edit
 * field on a detail page is one column of a four-column grid — about 130px once the tick and cross
 * buttons take their share — and the chip + name label clipped the CODE itself there
 * ("MH-SANGAM…"), which is the one part that must stay whole. In a box that small the honest thing
 * to show is the name a reader recognises; the code is still on hover (`title`), in the list, and in
 * the field's own read view. Wide pickers that pass no `optionLabelProp` keep the full chip label.
 */
export function territoryOption(t: TerritoryLike) {
  const name = String(t.name ?? '').trim();
  return { label: territoryLabel(t), searchLabel: territoryText(t), title: territoryText(t), selectedText: name || territoryText(t) };
}
